// Comparison landing page targeting "brik vs ridge" and
// "metal wallet comparison" searches. Keep the language factual and
// neutral; a useful comparison ranks and converts better than a hype page.
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import GuideVideo from "../components/GuideVideo";
import {
  makeBreadcrumbJsonLd,
  makeFaqJsonLd,
  makeVideoJsonLd,
} from "../seo/structuredData";
import heroPhoto from "../assets/newest-version/Full - nothing extended just basic.jpg";

const futura = "'Futura', sans-serif";

const COMPARISON_ROWS = [
  ["Holds cards", "Yes, 1 ID up front + 7-8 cards", "Yes", "Yes"],
  ["Holds cash", "Yes, elastic band in back", "Yes, clip or strap", "Yes"],
  ["Visible ID window", "Yes, quick-access front slot", "Usually no", "Sometimes"],
  ["Holds keys", "Yes, integrated keyring included", "No, separate carrier", "No"],
  ["Tracking", "Rechargeable tracking card included", "Add-on accessory", "Usually separate"],
  ["RFID protection", "Yes, main compartment", "Yes", "Rarely"],
  ["Material", "Anodized aluminum", "Aluminum or titanium", "Leather or fabric"],
  ["Price", "$69.99, everything included", "Varies, extras cost more", "Varies widely"],
];

const COMPARE_FAQ = [
  {
    q: "What is the main difference between The Brik and a Ridge-style wallet?",
    a: "Both are slim metal card wallets. The difference is what else they carry. The Brik includes an integrated keyring for your keys, a quick-access front ID slot, and a rechargeable tracking card, all in the base price. Ridge-style wallets focus on cards and cash, with key carriers and trackers sold as separate accessories.",
  },
  {
    q: "Does The Brik cost more than a Ridge-style wallet?",
    a: "The Metal Brik is $69.99 with the keyring and tracking card included. With Ridge-style wallets, the wallet, a key carrier, and a tracker are typically separate purchases, so a comparable setup usually costs more in total.",
  },
  {
    q: "Who should buy a Ridge-style wallet instead?",
    a: "If you only carry cards and a few bills, never carry keys with your wallet, and don't want tracking, a Ridge-style wallet is a solid minimal choice with many color options.",
  },
  {
    q: "Who should stick with a traditional bifold?",
    a: "If you carry many receipts, photos, or more than 10 cards, a leather bifold still holds the most. You give up slimness, RFID protection is rare, and tracking usually means dropping a separate tracker inside.",
  },
  {
    q: "Can I move my cards from a Ridge-style wallet to The Brik?",
    a: "Yes. The Brik holds 1 ID in the front slot and 7-8 cards in the main compartment, which covers the typical Ridge-style carry.",
  },
];

const sectionHeading = {
  fontFamily: futura,
  fontSize: "clamp(26px, 3vw, 36px)",
  margin: "0 0 12px",
  letterSpacing: "-0.5px",
  color: "#111",
};

const bodyText = {
  fontFamily: futura,
  fontSize: "16px",
  lineHeight: 1.65,
  color: "#444",
  margin: "0 0 16px",
};

const ctaButton = {
  display: "inline-block",
  padding: "16px 32px",
  background: "#ff7d1a",
  color: "white",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "1px",
  fontFamily: futura,
  textTransform: "uppercase",
  textDecoration: "none",
  boxShadow: "0 6px 20px rgba(255,125,26,0.35)",
};

const thStyle = {
  fontFamily: futura,
  padding: "14px 16px",
  fontSize: "14px",
  textTransform: "uppercase",
  letterSpacing: "0.8px",
  textAlign: "left",
  background: "#0c0c0c",
  color: "white",
};

const tdStyle = {
  fontFamily: futura,
  padding: "13px 16px",
  fontSize: "14.5px",
  lineHeight: 1.45,
  color: "#333",
  borderBottom: "1px solid #e6e6e6",
  verticalAlign: "top",
};

const compareVideoJsonLd = makeVideoJsonLd({
  name: "The Brik vs Ridge Wallet",
  description:
    "A side-by-side look at the Brik and a Ridge-style wallet: cards, keys, ID access, and tracking compared on camera.",
  poster: "/videos/brik-vs-ridge-wallet-video-poster.jpg",
  src: "/videos/brik-vs-ridge-wallet-video.mp4",
  duration: "PT44S",
});

const BrikVsRidge = () => {
  const faqJsonLd = makeFaqJsonLd(COMPARE_FAQ);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Brik vs Ridge", path: "/brik-vs-ridge" },
  ]);

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <SEO
        title="Brik vs Ridge: Cards, Keys, ID & Tracking Compared"
        description="An honest comparison of The Brik, Ridge-style metal wallets, and traditional bifolds: cards, cash, ID access, key carry, tracking, RFID, and price."
        path="/brik-vs-ridge"
        keywords="brik vs ridge, ridge wallet alternative, metal wallet comparison, ridge wallet vs, best metal wallet, wallet with key holder vs ridge"
        jsonLd={[faqJsonLd, breadcrumbJsonLd, compareVideoJsonLd]}
      />
      <Header />

      {/* Hero */}
      <section
        style={{
          background: "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "70px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "760px", margin: "0 auto" }}>
          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(32px, 4.2vw, 52px)",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              margin: "0 0 18px",
            }}
          >
            Brik vs Ridge: Cards, Keys, ID, and Tracking Compared
          </h1>
          <p
            style={{
              fontFamily: futura,
              fontSize: "17px",
              lineHeight: 1.55,
              color: "#cfcfcf",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Both are slim metal card wallets. The difference is what else they
            carry: The Brik includes an integrated keyring for your keys, a
            quick-access front ID slot, and a rechargeable tracking card, all
            in the $69.99 base price. Ridge-style wallets focus on cards and
            cash, with key carriers and trackers sold as separate accessories.
          </p>
        </div>
      </section>

      {/* Comparison table */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "28px" }}>
            Brik vs Ridge vs bifold: side by side
          </h2>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid #e6e6e6",
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>Feature</th>
                  <th style={{ ...thStyle, background: "#ff7d1a" }}>The Brik</th>
                  <th style={thStyle}>Ridge-style wallet</th>
                  <th style={thStyle}>Traditional bifold</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, i) => (
                  <tr key={row[0]} style={{ background: i % 2 ? "#fafafa" : "white" }}>
                    <td style={{ ...tdStyle, fontWeight: 700, color: "#111" }}>{row[0]}</td>
                    <td style={{ ...tdStyle, background: "rgba(255,125,26,0.06)" }}>{row[1]}</td>
                    <td style={tdStyle}>{row[2]}</td>
                    <td style={tdStyle}>{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p
            style={{
              fontFamily: futura,
              fontSize: "12.5px",
              color: "#999",
              margin: "14px 0 0",
            }}
          >
            "Ridge-style wallet" describes the popular category of two-plate
            metal card wallets with an elastic or screw-together frame, sold by
            several brands.
          </p>
          <p
            style={{
              fontFamily: futura,
              fontSize: "14px",
              color: "#666",
              margin: "10px 0 0",
            }}
          >
            Curious how the tracking row works in practice? See{" "}
            <a
              href="/wallet-with-built-in-tracker"
              style={{ color: "#ff7d1a", fontWeight: 600 }}
            >
              how the built-in tracker works
            </a>
            . Buying for school? See the{" "}
            <a
              href="/wallet-for-college-students"
              style={{ color: "#ff7d1a", fontWeight: 600 }}
            >
              college student setup
            </a>
            .
          </p>
        </div>
      </section>

      {/* Video comparison */}
      <GuideVideo
        heading="The comparison, on camera"
        caption="Both wallets in hand: what each carries and where they differ."
        src="/videos/brik-vs-ridge-wallet-video.mp4"
        poster="/videos/brik-vs-ridge-wallet-video-poster.jpg"
      />

      {/* Who should buy what */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "36px" }}>
            Who each wallet is right for
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "20px",
            }}
          >
            {[
              {
                title: "Get The Brik if...",
                highlight: true,
                text: "You carry keys with your wallet, want your ID visible for daily swipes, or lose things. Students, commuters, and anyone who has paid a lock-change fee get the most out of the integrated keyring and the included tracking card.",
              },
              {
                title: "Get a Ridge-style wallet if...",
                text: "You want the absolute thinnest cards-only carry, keep keys separately, and don't need tracking. You'll find wide color and material choices, with accessories available at extra cost.",
              },
              {
                title: "Keep a bifold if...",
                text: "You carry more than 10 cards, paper receipts, or photos, and slimness isn't a priority. Just know that RFID protection is uncommon and tracking means adding a separate tracker.",
              },
            ].map((c) => (
              <div
                key={c.title}
                style={{
                  background: c.highlight ? "#0c0c0c" : "white",
                  color: c.highlight ? "white" : "#111",
                  borderRadius: "14px",
                  padding: "26px 24px",
                  border: c.highlight ? "2px solid #ff7d1a" : "1px solid #e6e6e6",
                }}
              >
                <h3
                  style={{
                    fontFamily: futura,
                    fontSize: "18px",
                    margin: "0 0 10px",
                    color: c.highlight ? "#ff7d1a" : "#111",
                    letterSpacing: "-0.3px",
                  }}
                >
                  {c.title}
                </h3>
                <p
                  style={{
                    fontFamily: futura,
                    fontSize: "15px",
                    lineHeight: 1.6,
                    color: c.highlight ? "#cfcfcf" : "#444",
                    margin: 0,
                  }}
                >
                  {c.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Brik pitch */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div
          style={{
            maxWidth: "860px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "36px",
            alignItems: "center",
          }}
        >
          <div>
            <img
              src={heroPhoto}
              alt="The Metal Brik wallet closed, machined from black anodized aluminum"
              loading="lazy"
              style={{ width: "100%", borderRadius: "16px", display: "block" }}
            />
          </div>
          <div>
            <h2 style={sectionHeading}>Why the whole-carry approach wins</h2>
            <p style={bodyText}>
              A cards-only wallet solves a third of the problem. Your keys are
              still loose, your ID is still buried, and when the wallet slips
              between couch cushions it stays lost. The Brik ships with the
              keyring and the rechargeable tracking card in the box, so one
              $69.99 purchase covers the entire everyday carry.
            </p>
            <a href="/metal-wallet" style={ctaButton}>
              See the Metal Brik
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "30px" }}>
            Comparison questions, answered
          </h2>
          {COMPARE_FAQ.map((item) => (
            <details
              key={item.q}
              style={{
                background: "white",
                borderRadius: "12px",
                border: "1px solid #e6e6e6",
                padding: "18px 22px",
                marginBottom: "12px",
                fontFamily: futura,
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#111",
                }}
              >
                {item.q}
              </summary>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: "#555",
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BrikVsRidge;
